import React from 'react';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { RawButton } from '../../../../../CommonComponents/OPL/Button/Button.styles';
import { Card } from '../../../../../CommonComponents/OPL/Card/Card';
import { Dot } from '../../../../../CommonComponents/OPL/Dot/Dot';
import { Cell, HtmlTable } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../CommonComponents/OPL/Text/TruncatedText';
import { PerformanceTableRow } from '../../../PerformanceDetails/PerformanceDetails.styles';
import { ColumnKey, SortBy } from '../../IncomeDetails.meta';

interface Props {
  isRowExpanded: (id: string) => boolean;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  setSortBy: (sortBy: SortBy) => void;
  sortBy: SortBy;
  tableData: any;
  title: string;
  toggleRowExpanded: (id: string) => void;
}

export const IncomeDetailsContent = ({
  isRowExpanded,
  searchTerm,
  setSearchTerm,
  setSortBy,
  sortBy,
  tableData,
  title,
  toggleRowExpanded,
}: Props) => {
  const sortIcon =
    sortBy.sort === 'asc'
      ? 'fas fa-arrow-up'
      : sortBy.sort === 'desc'
      ? 'fas fa-arrow-down'
      : undefined;

  const onSort = (column: ColumnKey) => {
    let newSort: SortBy['sort'] = 'none';

    if (sortBy?.column !== column) {
      newSort = 'asc';
    } else if (sortBy?.sort === 'none') {
      newSort = 'asc';
    } else if (sortBy.sort === 'asc') {
      newSort = 'desc';
    } else if (sortBy.sort === 'desc') {
      newSort = 'none';
    }

    setSortBy({
      column,
      sort: newSort,
    });
  };

  return (
    <Card mt="16">
      <Box display="flex" flexDirection="column" p="12">
        <Text variant="cardTitle" mb="12">
          {title}
        </Text>

        <Input
          value={searchTerm}
          onChange={({ target: { value } }) => setSearchTerm(value)}
          placeholder="Search..."
          leftIcon="fa fa-search"
          aria-label="Search"
        />
      </Box>

      <HtmlTable variant="embedded" columnSpacing="small" maxHeight={600} fixedHead>
        <table>
          <thead>
            <tr>
              {tableData.columns.map((column, index) => (
                <th key={index}>
                  <RawButton
                    onClick={() => onSort(column.key)}
                    aria-label={`Sort by ${column.header}`}
                    css="width: 100%"
                  >
                    <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                      <Text variant="tableHeader" textAlign={column.align} width="100%">
                        {column.header}
                      </Text>

                      {sortBy.column === column.key && (
                        <Icon className={sortIcon} color="neutral.500" />
                      )}
                    </Box>
                  </RawButton>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tableData.rows.length === 0 ? (
              <tr>
                <td colSpan={tableData.columns.length}>
                  <Box height={200} />
                </td>
              </tr>
            ) : (
              tableData.rows.map(({ cells, childRows, id, color }) => {
                const hasChildRows = childRows && childRows.length > 0;
                const isExpanded = id ? isRowExpanded(id) : false;

                const tableRowVariant = hasChildRows ? 'clickable' : 'default';

                return (
                  <React.Fragment key={id}>
                    <PerformanceTableRow
                      isExpanded={isExpanded}
                      onClick={hasChildRows && id ? () => toggleRowExpanded(id) : undefined}
                      variant={tableRowVariant}
                    >
                      {cells.map((cell, index) => (
                        <Cell
                          key={index}
                          isFlex={index === 0}
                          style={index === 0 ? { minWidth: 200 } : undefined}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent={cell.align === 'left' ? 'flex-start' : 'flex-end'}
                          >
                            {index === 0 && hasChildRows && (
                              <Icon
                                className={
                                  isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
                                }
                                color="neutral.400"
                                mr={isExpanded ? 8 : 12}
                              />
                            )}

                            {index === 0 ? (
                              <>
                                <Dot bg={color || undefined} mr="8" />

                                <TruncatedText
                                  fontWeight={600}
                                  color="neutral.700"
                                  textAlign={cell.align}
                                  width="100%"
                                >
                                  {cell.text}
                                </TruncatedText>
                              </>
                            ) : (
                              <Text
                                fontWeight={600}
                                color="neutral.700"
                                textAlign={cell.align}
                                width="100%"
                                variant="tabular"
                              >
                                {cell.text}
                              </Text>
                            )}
                          </Box>
                        </Cell>
                      ))}
                    </PerformanceTableRow>

                    {isExpanded &&
                      childRows &&
                      childRows.map((childRow, key) => (
                        <PerformanceTableRow key={key}>
                          {childRow.cells.map((childCell, index) => (
                            <Cell key={index} isFlex={index === 0}>
                              {index === 0 ? (
                                <TruncatedText textAlign={childCell.align} width="100%" pl="40">
                                  {childCell.text}
                                </TruncatedText>
                              ) : (
                                <Text
                                  textAlign={childCell.align}
                                  width="100%"
                                  variant="tabular"
                                  css="white-space: nowrap"
                                >
                                  {childCell.text}
                                </Text>
                              )}
                            </Cell>
                          ))}
                        </PerformanceTableRow>
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </HtmlTable>
    </Card>
  );
};
